import { useState, useEffect, memo, cloneElement, useLayoutEffect } from 'react'
import type { NamedExoticComponent, PropsWithChildren, ReactElement } from 'react'
import { setLocale, getLocale } from 'umi'
import type { History } from 'history-with-query'

import { StaticErrorBoundary } from '@/wrappers/ErrorBoundary'

import IconSettings from '@salesforce/design-system-react/components/icon-settings'

import MessageBox from '@/components/Prompt'

import { getBrowserVersionPromptStatus } from '@/utils/browserVersion'

import PromptContainer from '@/components/Prompt/Container'
import RouteMeta from '../RouteMeta'

import styles from './index.less'

interface IRootContainerProps {
  routes: Route[]
  history: History
  [k: string]: any
}

const RootContainer: NamedExoticComponent<PropsWithChildren<IRootContainerProps>> = memo(
  (props) => {
    const { routes, history, children } = props

    const [language, setLanguage] = useState(() => {
      let locale = 'zh-CN'
      /** 防止用户浏览器禁止读取 */
      try {
        locale = getLocale()
        // eslint-disable-next-line no-empty
      } catch (err) {
        console.log('get local storage failed', err)
      }
      return locale
    })

    useLayoutEffect(() => {
      const showPrompt = getBrowserVersionPromptStatus()
      if (!showPrompt) return
      MessageBox.default({
        hideFooter: true,
        title: '提示',
        disableClose: true,
        content: (
          <div className="pre-line">{`浏览器建议：
        一般来说，所有现代web浏览器都支持SameSite cookie属性，但市场上仍存在少量旧web浏览器（<7%），由于它们与现代安全技术广泛不兼容，因此对我们的产品构成了重大威胁。为了更好地保护我们的客户，我们将停止支持过时的web浏览器，并限制他们的请求。Salesforce有责任保持高安全标准，我们建议您使用以下浏览器及版本：

        Chrome: 80+
        Edge: 80+
        Firefox: 80+
        Opera: 80+
        Safari: 13+
        Chrome Android: 63+
        Firefox Android: 63+
        Safari on iOS: 12.2+

        来自不受支持的web浏览器的请求将被拒绝，并显示错误，请选择正确版本的浏览器以继续使用Salesforce产品。`}</div>
        ),
      })
    }, [])

    useEffect(() => {
      let el: HTMLElement | null = window.document.body
      el.style.overflow = 'overlay'
      const bodyObserver = new MutationObserver((mutation) => {
        const attributeUpdater = mutation.find(
          (mutate) => mutate.type === 'attributes' && mutate.attributeName === 'style',
        )
        if (!attributeUpdater) return
        const overflowAttribute = el?.style.overflow
        if (!el) return
        const shouldReplaceAttr = ['inherit', 'auto', 'initial', 'visible', undefined, null]
        if (!shouldReplaceAttr.includes(overflowAttribute)) return
        el!.style.overflow = 'overlay'
      })

      bodyObserver.observe(el, { attributes: true })

      return () => {
        el = null
      }
    }, [])

    return (
      <StaticErrorBoundary>
        <RouteMeta routes={routes} history={history}>
          <IconSettings iconPath="/assets/icons">
            <>
              <PromptContainer />
              {cloneElement(children as ReactElement, props) as ReactElement}
            </>
          </IconSettings>
          <div id="current-language" className={styles['hide-language']}>
            {language}
          </div>
        </RouteMeta>
      </StaticErrorBoundary>
    )
  },
)

export default RootContainer
